import React from 'react';

import { ButtonGroup } from '@/components/shared/buttons/button-group';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { styled } from '@/stitches.config';
import { IButton } from '@/types/shared';

const StyledHeadline = styled('div', {
  display: 'grid',
  gap: '$space-6',
  textAlign: 'center',
});

const HeadlineWrap = styled('div', {
  marginBottom: '$space-6',
  '@sm': {
    marginBottom: '$space-10',
  },
});

interface HeadingLevel3Props
  extends React.ComponentProps<typeof StyledHeadline> {
  /**
   * The title of the heading.
   */
  title?: string;

  /**
   * The optional description to display below the title.
   */
  description?: string;

  /**
   * The optional remark to display below the description.
   */
  remark?: string;

  /**
   * The array of buttons to render.
   */
  buttons?: IButton[];

  /**
   * Indicates if the theme is dark.
   */
  isDarkTheme?: boolean;

  /**
   * The theme for the component.
   */
  theme?:
    | 'dark'
    | 'blue'
    | 'blueDark'
    | 'green'
    | 'grayLight'
    | 'gray'
    | 'grayDark'
    | 'red';

  /**
   * The data tracking value for the heading.
   */
  dataTrack?: string;

  /**
   * The data tracking section for the heading.
   */
  dataTrackSection?: string;

  /**
   * The data tracking value for the heading.
   */
  dataTrackValue?: string;

  /**
   * The data test value for the heading.
   */
  dataTest?: string;

  /**
   * The size of the heading.
   */
  size?: 'default' | 'sm';
}
/**
 * Renders a level 3 heading component with an optional title, description, remark, and buttons.
 *
 * @param {HeadingLevel3Props} props - The props for the HeadingLevel3 component.
 * @param {string} props.title - The title of the heading.
 * @param {string} props.description - The optional description to display below the title.
 * @param {string} props.remark - The optional remark to display below the description.
 * @param {IButton[]} props.buttons - The array of buttons to render.
 * @param {boolean} props.isDarkTheme - Indicates if the theme is dark.
 * @param {string} props.theme - The theme for the component.
 * @param {string} props.dataTrack - The data tracking value for the heading.
 * @param {string} props.dataTrackSection - The data tracking section for the heading.
 * @param {string} props.dataTrackValue - The data tracking value for the heading.
 * @param {string} props.dataTest - The data test value for the heading.
 * @param {('default' | 'sm')} props.size - The size of the heading.
 * @param {...React.HTMLAttributes<HTMLDivElement>} props.rest - Additional attributes for the component.
 * @return {React.ReactElement} The rendered level 3 heading component.
 */
export const HeadingLevel3: React.FC<HeadingLevel3Props> = ({
  title,
  description,
  remark,
  buttons = [],
  isDarkTheme = false,
  theme,
  dataTrack,
  dataTrackSection,
  dataTrackValue,
  dataTest = 'heading',
  size = 'default',
  ...rest
}: HeadingLevel3Props) => {
  const getTitleColor = () => {
    if (isDarkTheme) {
      return 'text-white';
    }
    switch (theme) {
      case 'gray':
      case 'dark':
      case 'grayDark':
      case 'red':
        return 'text-white';
      case 'blue':
        return 'text-blue';
      case 'blueDark':
        return 'text-blue-dark';
      case 'green':
        return 'text-green';
      default:
        return 'text-gray-darkest';
    }
  };

  const getDescriptionColor = () => {
    if (isDarkTheme) {
      return 'text-white';
    }
    switch (theme) {
      case 'gray':
      case 'dark':
      case 'grayDark':
      case 'red':
        return 'text-white';
      default:
        return 'text-gray-darkest';
    }
  };

  const hasButtons = buttons.some((item) => item.label !== '');

  return (
    <>
      {!!(title || hasButtons) && (
        <HeadlineWrap
          css={
            !title
              ? {
                  marginBottom: '$space-0',
                  marginTop: '$space-6',
                  '@sm': {
                    marginTop: '$space-10',
                  },
                }
              : undefined
          }
        >
          <Container>
            <StyledHeadline {...rest} data-test={dataTest}>
              {!!title && (
                <Text
                  as="h2"
                  font="bold"
                  size={size === 'default' ? 'text-sub-h1' : 'text-sub-h2'}
                  variant={getTitleColor()}
                  wrap
                  dangerouslySetInnerHTML={{ __html: title }}
                  data-test={`${dataTest}_title`}
                />
              )}
              {!!(description || remark) && (
                <Box css={{ maxWidth: 616, mx: 'auto' }}>
                  {!!description && (
                    <Text
                      as="div"
                      size="text-body-1"
                      variant={getDescriptionColor()}
                      wrap
                      dangerouslySetInnerHTML={{
                        __html: description,
                      }}
                      data-test={`${dataTest}_description`}
                    />
                  )}
                  {!!remark && (
                    <Text
                      as="div"
                      size="text-caption"
                      variant={getDescriptionColor()}
                      wrap
                      dangerouslySetInnerHTML={{ __html: remark }}
                      data-test={`${dataTest}_remark`}
                      css={{ marginTop: '$space-2' }}
                    />
                  )}
                </Box>
              )}
              {!!hasButtons && (
                <ButtonGroup
                  justify="center"
                  buttons={buttons}
                  variant="button-red"
                  isMinWidth
                  tracking={{
                    dataTrack: dataTrack as string,
                    dataTrackSection: dataTrackSection,
                    dataTrackValue: dataTrackValue,
                  }}
                  test={{
                    dataTest: `${dataTest}_button`,
                  }}
                />
              )}
            </StyledHeadline>
          </Container>
        </HeadlineWrap>
      )}
    </>
  );
};

HeadingLevel3.displayName = 'HeadingLevel3';
